import { type DashboardType, PlanLimitTypes } from 'nocodb-sdk'
import { DlgDashboardCreate } from '#components'

export const useDashboardStore = defineStore('dashboard', () => {
  const { $api, $e, $poller } = useNuxtApp()

  const { ncNavigateTo } = useGlobal()

  const { isEEFeatureBlocked, showDashboardPlanLimitExceededModal, showUpgradeForEEFeature, updateStatLimit } = useEeConfig()

  const { refreshCommandPalette } = useCommandPalette()

  const router = useRouter()

  const route = useRoute()

  const baseStore = useBases()

  const { loadProject } = baseStore

  const { activeProjectId, bases } = storeToRefs(baseStore)

  const { activeWorkspaceId, isSharedBase } = storeToRefs(useWorkspace())

  // State
  const dashboards = ref<Map<string, DashboardType[]>>(new Map())

  const sharedDashboardState = reactive({
    password: '',
    activeProjectId: null,
  })

  const isEditingDashboard = ref(false)

  const activeBaseDashboards = computed(() => {
    const id = activeProjectId.value || sharedDashboardState.activeProjectId
    if (!id) return []
    return dashboards.value.get(id) || []
  })

  const activeDashboardId = computed(() => route.params.dashboardId as string)

  const activeDashboard = computed(() => {
    const id = activeProjectId.value || sharedDashboardState.activeProjectId
    if (!id) return null
    return dashboards.value.get(id)?.find((a) => a.id === activeDashboardId.value || a.uuid === activeDashboardId.value) || null
  })

  const activeDashboardUrlSlug = computed(() => {
    return route.params.slugs?.[0] || ''
  })

  const activeDashboardReadableUrlSlug = computed(() => {
    if (!activeDashboard.value || sharedDashboardState.activeProjectId) return ''

    return toReadableUrlSlug([activeDashboard.value.title])
  })

  const loadDashboards = async ({ baseId, force = false }: { baseId: string; force?: boolean }) => {
    // In shared base we are not showing dashboards, so better to avoid api call
    if (!activeWorkspaceId.value || isSharedBase.value) return []

    if (dashboards.value.has(baseId) && !force) {
      return dashboards.value.get(baseId) || []
    }

    try {
      const response = (await $api.internal.getOperation(activeWorkspaceId.value, baseId, {
        operation: 'dashboardList',
      })) as DashboardType[]

      if (ncIsArray(response)) {
        dashboards.value.set(baseId, response)
        return response
      } else {
        return []
      }
    } catch (e) {
      console.error(e)
      message.error(await extractSdkResponseErrorMsgv2(e as any))
      return []
    }
  }

  const loadDashboard = async (dashboardId: string) => {
    if (!activeProjectId.value || !dashboardId || !activeWorkspaceId.value) return null

    let dashboard: null | DashboardType = null

    if (dashboards.value.get(activeProjectId.value)?.find((a) => a.id === dashboardId)) {
      dashboard = (dashboards.value.get(activeProjectId.value) ?? []).find((a) => a.id === dashboardId) || null
    }

    try {
      if (!dashboard) {
        dashboard = (await $api.internal.getOperation(activeWorkspaceId.value, activeProjectId.value, {
          operation: 'dashboardGet',
          dashboardId,
        })) as unknown as DashboardType

        const baseDashboards = dashboards.value.get(activeProjectId.value) || []
        const filtered = baseDashboards.filter((a) => a.id !== dashboardId)
        filtered.push(dashboard)
        filtered.sort((a, b) => a.order - b.order)
        dashboards.value.set(activeProjectId.value, filtered)

        return dashboard
      }
    } catch (e) {
      console.error(e)
      message.error(await extractSdkResponseErrorMsgv2(e as any))
      ncNavigateTo({
        workspaceId: activeWorkspaceId.value,
        baseId: activeProjectId.value,
      })
      return null
    }
  }

  const loadSharedDashboard = async (dashboardId: string, password: string) => {
    let dashboard: null | any = null
    sharedDashboardState.password = password
    dashboard = await $api.public.sharedDashboardMetaGet(dashboardId, {
      headers: {
        'xc-password': password ?? sharedDashboardState.password,
      },
    })

    const base_id = dashboard.base_id
    sharedDashboardState.activeProjectId = base_id
    const baseDashboards = dashboards.value.get(base_id) || []
    const filtered = baseDashboards.filter((a) => a.id !== dashboardId)
    filtered.push(dashboard)
    filtered.sort((a, b) => a.order - b.order)
    dashboards.value.set(base_id, filtered)

    return dashboard
  }

  const createDashboard = async (baseId: string, dashboardData: Partial<DashboardType>) => {
    if (!activeWorkspaceId.value) return null
    try {
      const created = await $api.internal.postOperation(
        activeWorkspaceId.value,
        baseId,
        {
          operation: 'dashboardCreate',
        },
        dashboardData,
      )
      updateStatLimit(PlanLimitTypes.LIMIT_DASHBOARD_PER_WORKSPACE, 1)

      const baseDashboards = dashboards.value.get(baseId) || []
      baseDashboards.push({
        ...created,
        ___is_new: true,
      } as any)
      dashboards.value.set(baseId, baseDashboards)

      await refreshCommandPalette()

      ncNavigateTo({
        workspaceId: activeWorkspaceId.value,
        baseId: activeProjectId.value,
        dashboardId: created.id,
        dashboardTitle: created.title,
      })
      isEditingDashboard.value = true

      return created
    } catch (e) {
      console.error(e)
      message.error(await extractSdkResponseErrorMsgv2(e as any))
      return null
    }
  }

  const updateDashboard = async (
    baseId: string,
    dashboardId: string,
    updates: Partial<DashboardType>,
    options?: {
      skipNetworkCall?: boolean
    },
  ) => {
    if (!activeWorkspaceId.value) return null
    try {
      const dashboard = dashboards.value.get(baseId)?.find((a) => a.id === dashboardId)
      const updated = options?.skipNetworkCall
        ? {
            ...dashboard,
            ...updates,
          }
        : await $api.internal.postOperation(
            activeWorkspaceId.value,
            baseId,
            {
              operation: 'dashboardUpdate',
              dashboardId,
            },
            updates,
          )

      const baseDashboards = dashboards.value.get(baseId) || []
      const index = baseDashboards.findIndex((a) => a.id === dashboardId)

      if (index !== -1) {
        baseDashboards[index] = updated as any
        dashboards.value.set(baseId, baseDashboards)
      }

      await refreshCommandPalette()

      if (!options?.skipNetworkCall) {
        $e('a:dashboard:update')
      }

      return updated
    } catch (e) {
      console.error(e)
      message.error(await extractSdkResponseErrorMsgv2(e as any))
      return null
    }
  }

  const deleteDashboard = async (baseId: string, dashboardId: string) => {
    if (!activeWorkspaceId.value) return null
    try {
      const dashboard = dashboards.value.get(baseId)?.find((a) => a.id === dashboardId)
      const widgetCount = (dashboard as any)?.widgets?.length || 0

      await $api.internal.postOperation(
        activeWorkspaceId.value,
        baseId,
        {
          operation: 'dashboardDelete',
        },
        {
          dashboardId,
        },
      )

      updateStatLimit(PlanLimitTypes.LIMIT_DASHBOARD_PER_WORKSPACE, -1)

      $e('a:dashboard:delete', { widget_count: widgetCount })

      // Update local state
      const baseDashboards = dashboards.value.get(baseId) || []
      const filtered = baseDashboards.filter((a) => a.id !== dashboardId)
      dashboards.value.set(baseId, filtered)

      if (activeDashboardId.value === dashboardId) {
        const nextDashboard = filtered[0]
        if (nextDashboard) {
          ncNavigateTo({
            workspaceId: activeWorkspaceId.value,
            baseId: activeProjectId.value,
            dashboardId: nextDashboard.id,
            dashboardTitle: nextDashboard.title,
          })
        }
      }

      await refreshCommandPalette()

      if (!filtered.length) {
        ncNavigateTo({
          workspaceId: activeWorkspaceId.value,
          baseId: activeProjectId.value,
        })
      }

      return true
    } catch (e) {
      console.error(e)
      message.error(await extractSdkResponseErrorMsgv2(e as any))
      return false
    }
  }

  const openDashboard = async (dashboard: DashboardType) => {
    if (!dashboard.base_id || !dashboard.id) return

    let base = bases.value.get(dashboard.base_id)
    if (!base) {
      await loadProject(dashboard.base_id)
      base = bases.value.get(dashboard.base_id)
      if (!base) throw new Error('Base not found')
    }

    let workspaceIdOrType = activeWorkspaceId.value
    if (['nc', 'base'].includes(route.params.typeOrId as string)) {
      workspaceIdOrType = route.params.typeOrId as string
    }

    let baseIdOrBaseId = base.id
    if (['base'].includes(route.params.typeOrId as string)) {
      baseIdOrBaseId = route.params.baseId as string
    }

    $e('c:dashboard:open')

    ncNavigateTo({
      workspaceId: workspaceIdOrType,
      baseId: baseIdOrBaseId,
      dashboardId: dashboard.id,
      dashboardTitle: dashboard.title,
    })
  }

  const duplicateDashboard = async (baseId: string, dashboardId: string, extra: any, onProgress?: (status: string) => void) => {
    if (!activeWorkspaceId.value) return null

    try {
      const jobData = await $api.dbDashboard.duplicateDashboard(baseId, dashboardId, {
        extra,
      })

      return new Promise((resolve, reject) => {
        $poller.subscribe(
          { id: jobData.id! },
          async (data: {
            id: string
            status?: string
            data?: {
              error?: {
                message: string
              }
              message?: string
              result?: any
            }
          }) => {
            if (data.status !== 'close') {
              onProgress?.(data.status || 'processing')

              if (data.status === JobStatus.COMPLETED) {
                updateStatLimit(PlanLimitTypes.LIMIT_DASHBOARD_PER_WORKSPACE, 1)

                // Refresh dashboards list to include the new duplicate
                await loadDashboards({ baseId, force: true })

                const duplicatedDashboard = data.data?.result
                if (duplicatedDashboard && duplicatedDashboard.id) {
                  ncNavigateTo({
                    workspaceId: activeWorkspaceId.value,
                    baseId,
                    dashboardId: duplicatedDashboard.id,
                    dashboardTitle: duplicatedDashboard.title,
                  })
                }
                await refreshCommandPalette()

                $e('a:dashboard:duplicate')
                resolve(data.data?.result)
              } else if (data.status === JobStatus.FAILED) {
                const errorMsg = data.data?.error?.message || 'There was an error duplicating the dashboard.'
                message.error(errorMsg)
                await refreshCommandPalette()
                reject(new Error(errorMsg))
              }
            }
          },
        )
      })
    } catch (e) {
      console.error(e)
      message.error(await extractSdkResponseErrorMsgv2(e as any))
      throw e
    }
  }

  async function openNewDashboardModal({
    baseId,
    e,
    loadBasesOnClose,
    scrollOnCreate,
    navigateToNewDashboard = true,
  }: {
    baseId?: string
    e?: string
    loadBasesOnClose?: boolean
    scrollOnCreate?: boolean
    navigateToNewDashboard?: boolean
  }) {
    if (!baseId || showDashboardPlanLimitExceededModal()) return

    if (isEEFeatureBlocked.value) {
      showUpgradeForEEFeature('Dashboards')
      return
    }
    const isDlgOpen = ref(true)

    const { close } = useDialog(DlgDashboardCreate, {
      'modelValue': isDlgOpen,
      'baseId': baseId,
      'onUpdate:modelValue': () => closeDialog(),
      'onCreated': async (dashboard: DashboardType) => {
        closeDialog()

        if (loadBasesOnClose) {
          await loadDashboards({ baseId, force: true })
        }

        $e(e ?? 'a:dashboard:create')

        if (!dashboard) return

        if (scrollOnCreate) {
          setTimeout(() => {
            const newDashboardDom = document.querySelector(`[data-dashboard-id="${dashboard.id}"]`)
            if (!newDashboardDom) return

            // Scroll to the script node
            newDashboardDom?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
          }, 1000)
        }

        if (navigateToNewDashboard) {
          ncNavigateTo({
            workspaceId: activeWorkspaceId.value,
            baseId,
            dashboardId: dashboard.id,
            dashboardTitle: dashboard.title,
          })
        }
      },
    })

    function closeDialog() {
      isDlgOpen.value = false
      close(1000)
    }
  }

  const dashboardShare = async (
    baseId: string,
    dashboardId: string,
    shareData: {
      password?: string | null
      custom_url_path?: string | null
      uuid?: null
    } = {},
  ) => {
    if (!activeWorkspaceId.value) return

    try {
      const response = await $api.internal.postOperation(
        activeWorkspaceId.value,
        baseId,
        {
          operation: 'dashboardShare',
        },
        {
          id: dashboardId,
          ...shareData,
        },
      )

      $e('a:dashboard:share', {
        has_password: !!shareData.password,
        has_custom_url: !!shareData.custom_url_path,
      })

      const baseDashboards = dashboards.value.get(baseId)
      if (baseDashboards) {
        const dashboardIndex = baseDashboards.findIndex((d) => d.id === dashboardId)
        if (dashboardIndex !== -1) {
          baseDashboards[dashboardIndex] = { ...baseDashboards[dashboardIndex], ...response }
          dashboards.value.set(baseId, [...baseDashboards])
        }
      }

      return response
    } catch (error) {
      console.error('Error with dashboard sharing:', error)
      throw error
    }
  }

  // Watch for active dashboard changes
  watch(activeDashboardId, async (dashboardId) => {
    if (!activeProjectId.value) return
    if (dashboardId) {
      await loadDashboard(dashboardId)
    }
  })

  /**
   * Keeps the browser URL slug in sync with the dashboard's readable slug.
   * Triggers only when:
   * - The current browser URL slug is missing, OR
   * - The browser URL slug does not match the dashboard's readable slug.
   */
  watch(
    [activeDashboardReadableUrlSlug, activeDashboardUrlSlug],
    ([newActiveDashboardReadableUrlSlug, newActiveDashboardUrlSlug]) => {
      if (!newActiveDashboardReadableUrlSlug || newActiveDashboardUrlSlug === newActiveDashboardReadableUrlSlug) return

      const slugs = (route.params.slugs as string[]) || []

      const newSlug = [newActiveDashboardReadableUrlSlug]

      if (slugs.length > 1) {
        newSlug.push(...slugs.slice(1))
      }

      router.replace({
        name: 'index-typeOrId-baseId-index-dashboards-dashboardId-slugs',
        params: {
          ...route.params,
          slugs: newSlug,
        },
        query: route.query,
        force: true,
      })
    },
    {
      immediate: true,
      flush: 'post',
    },
  )

  onMounted(async () => {
    if (!activeDashboardId.value || !activeProjectId.value) {
      return
    }
    loadDashboard(activeDashboardId.value)
  })

  return {
    // State
    dashboards,
    activeDashboard,
    isEditingDashboard,
    sharedDashboardState,
    loadSharedDashboard,

    // Getters
    activeBaseDashboards,
    activeDashboardId,

    // Actions
    loadDashboards,
    loadDashboard,
    createDashboard,
    updateDashboard,
    deleteDashboard,
    openDashboard,
    openNewDashboardModal,
    duplicateDashboard,
    dashboardShare,
  }
})

// Enable HMR
if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useDashboardStore, import.meta.hot))
}
